import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { InneUstawieniaWyspy, OdstepyTabel } from '@/components/OdstepyTabel';
import { PlikiLogowSps } from '@/components/PlikiLogowSps';
import { ResetInstancji } from '@/components/ResetInstancji';
import { TrybIBazaWyspy } from '@/components/TrybIBazaWyspy';
import { UsunieciWApi } from '@/components/UsunieciWApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { fetchBranding, logoUrl, updateBranding } from '@/lib/branding-api';
import { useI18n } from '@/lib/i18n';

/**
 * Ustawienia INSTANCJI — na razie sam branding (PLAN §8a: „logo w powłoce i na ekranie
 * logowania, samoobsługowo").
 *
 * Osobny ekran, a nie sekcja na Kontach: Konta odpowiadają na pytanie „kto ma dostęp",
 * a to jest pytanie „jak ta instancja wygląda". Ekran jest przygotowany na kolejne
 * ustawienia instancji, które i tak przyjdą (§8a wymienia jeszcze cooldown i snapshoty).
 */

/** Ten sam limit, który egzekwuje serwer — po to, żeby odmowa przyszła przed wysłaniem pliku. */
const MAX_KB = 512;

export function Settings() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);

  const { data, isPending } = useQuery({ queryKey: ['branding'], queryFn: fetchBranding });

  const save = useMutation({
    mutationFn: updateBranding,
    onSuccess: () => {
      toast.success(t('set.saved'));
      // powłoka i ekran logowania czytają ten sam klucz — bez unieważnienia logo w nagłówku
      // zmieniłoby się dopiero po przeładowaniu strony
      void qc.invalidateQueries({ queryKey: ['branding'] });
      setName(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pick = (file: File) => {
    if (file.size > MAX_KB * 1024) {
      toast.error(`${t('set.tooBig')} ${Math.ceil(file.size / 1024)} kB / ${MAX_KB} kB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => save.mutate({ logoDataUrl: String(reader.result) });
    reader.onerror = () => toast.error(t('set.readFailed'));
    // wysyłamy jako `data:` URL — serwer i tak sprawdza TYP PO BAJTACH, nie po nagłówku
    reader.readAsDataURL(file);
  };

  const displayed = name ?? (data?.customName ? data.displayName : '');

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">{t('set.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('set.subtitle')}</p>
      </header>

      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <ImageIcon className="size-4" />
          {t('set.brandingTitle')}
        </h2>

        {isPending ? (
          <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-border bg-muted/30">
                {data?.hasLogo ? (
                  <img
                    src={logoUrl(data.logoVersion)}
                    alt={data.displayName}
                    className="max-h-16 max-w-44 object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">{t('set.noLogo')}</span>
                )}
              </div>

              <div className="space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  // SVG świadomie POMINIĘTY także tutaj — serwer go odrzuca, bo mógłby nieść
                  // skrypt wykonywany na ekranie logowania. Lista tu i tam musi się zgadzać.
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) pick(f);
                    e.target.value = '';
                  }}
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" disabled={save.isPending} onClick={() => fileRef.current?.click()}>
                    {save.isPending ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                    {t('set.upload')}
                  </Button>
                  {data?.hasLogo && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={save.isPending}
                      onClick={() => save.mutate({ removeLogo: true })}
                    >
                      <Trash2 className="size-4" />
                      {t('set.removeLogo')}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t('set.logoHint')}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-2 border-t border-border pt-4">
              <label className="flex min-w-64 flex-col gap-1 text-xs">
                <span className="text-muted-foreground">{t('set.name')}</span>
                <Input
                  value={displayed}
                  placeholder={data?.customName ? undefined : data?.displayName}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <Button
                size="sm"
                disabled={save.isPending || name === null}
                onClick={() => save.mutate({ displayName: name })}
              >
                {t('set.saveName')}
              </Button>
              {/* puste pole = powrót do nazwy produktu; mówimy to wprost, bo inaczej wygląda
                  jak brak nazwy */}
              <p className="pb-2 text-xs text-muted-foreground">{t('set.nameHint')}</p>
            </div>
          </>
        )}
      </section>

      {/* ⚠️ DWIE SEKCJE, NIE JEDNA. Odstępy tabel to osobny temat od reszty ustawień wyspy —
          wrzucone razem sprawiały, że korekty zegara i odstępu spisu kopii szukało się w tabeli
          tabel SPS. Ustawienia eksploatacyjne trzymamy tutaj, a nie w panelu ponad instancjami:
          agent zgłasza się do instancji i to ona mu je poda. */}
      {/* Ustawienie INSTANCJI, nie wyspy — stoi przy brandingu, przed ustawieniami wysp. */}
      <UsunieciWApi />

      {/* ⚠️ Model wdrożenia PRZED pozostałymi ustawieniami: od niego zależy, co znaczą tamte. */}
      <TrybIBazaWyspy />

      {/*
        ⚠️ RESET CAŁEJ INSTANCJI STOI TU, a nie w Agentach (przeniesione 01.09.2026 na żądanie):
        dotyczy instancji, nie agentów, a ekran Agentów odwiedza się codziennie. Usuwanie POJEDYNCZEJ
        wyspy zostaje tam, gdzie stoi lista wysp z licznikami — bo tam widać, którą usunąć.
      */}
      <ResetInstancji />
      <InneUstawieniaWyspy />
      <OdstepyTabel />
      {/* ⚠️ Dozór plików logów SPS — jedyna rzecz w portalu, która widzi awarię SAMEGO SupplyPointa. */}
      <PlikiLogowSps />
    </div>
  );
}
